import { Button, Divider, Form, Spin, Timeline, Typography } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import { administratorsListAction } from '~Administrators/actions';
import { administratorsSelector } from '~Administrators/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import IAdviser from '~Api/Adviser/IAdviser';
import INote from '~Api/Adviser/INote';
import {
    adviserGetAction,
    adviserNotesAddAction,
    adviserNotesListAction,
} from '~Advisers/actions';
import {
    adviserNotesSelector,
    adviserSelector,
} from '~Advisers/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';

interface IState {
    note: string;
}

interface IMatch {
    adviserUuid: string;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IPropsSelector {
    administrators: IDictionary<IAdministrator>;
    adviser: IAdviser;
    notes: IDictionary<INote>;
}

interface IPropsDispatch {
    administratorsList: () => void;
    adviserGet: () => void;
    noteAdd: (note: string) => void;
    notesList: () => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class Notes extends React.Component<Props, IState> {
    public state: IState = {
        note: null,
    };

    constructor(props: Props) {
        super(props);

        this.onChangeNote = this.onChangeNote.bind(this);
        this.onClickAddNote = this.onClickAddNote.bind(this);
    }

    public componentDidMount() {
        const { administrators, adviser } = this.props;

        if (!adviser) {
            this.props.adviserGet();
        }

        if (!administrators) {
            this.props.administratorsList();
        }

        this.props.notesList();
    }

    public render(): JSX.Element {
        const { administrators, adviser, match, notes } = this.props;
        const { note } = this.state;

        if (!administrators || !adviser || !notes) {
            return (
                <Layout adviserUuid={match.params.adviserUuid} section='notes'>
                    <Typography.Title level={2}>Notes</Typography.Title>
                    <Spin/>
                </Layout>
            );
        }

        const unsavedNote: INote = _.find(notes, (loopNote: INote) => !loopNote.uuid);

        const unsavedNoteBlock: JSX.Element = unsavedNote && (
            <React.Fragment>
                <Typography.Text type='secondary'>{administrators[unsavedNote.administratorUuid].name}</Typography.Text><br/>
                <Typography.Text>{unsavedNote.note}</Typography.Text>
            </React.Fragment>
        );

        const notesBlock: JSX.Element[] = _.sortBy(_.filter(notes, (loopNote: INote) => !!loopNote.uuid), 'createdTime').map((loopNote: INote) => (
            <Timeline.Item key={loopNote.uuid} label={dayjs(loopNote.createdTime).format('YYYY-MM-DD - HH:mm')}>
                <Typography.Text type='secondary'>{administrators[loopNote.administratorUuid].name}</Typography.Text><br/>
                <Typography.Text>{loopNote.note}</Typography.Text>
            </Timeline.Item>
        ));

        return (
            <Layout adviserUuid={match.params.adviserUuid} section='notes'>
                <Typography.Title level={2}>Notes</Typography.Title>
                <Form.Item label='Note' className='note'>
                    <TextArea disabled={!!unsavedNote} onChange={this.onChangeNote} rows={15} value={note} />
                </Form.Item>
                <Button
                    className='add-note'
                    disabled={!note || !!unsavedNote}
                    onClick={this.onClickAddNote}
                    type='primary'
                >
                    Add Note
                </Button>
                {notesBlock.length > 0 && <Divider/>}
                <Timeline mode='left' pending={unsavedNoteBlock} reverse={true}>
                    {notesBlock}
                </Timeline>
            </Layout>
        );
    }

    private onChangeNote(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.setState({
            note: event.target.value,
        });
    }

    private onClickAddNote(): void {
        const { note } = this.state;

        this.setState({
            note: null,
        });

        this.props.noteAdd(note);
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        administrators: administratorsSelector(state),
        adviser: adviserSelector(state, ownProps.match.params.adviserUuid),
        notes: adviserNotesSelector(state, ownProps.match.params.adviserUuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        administratorsList: () => dispatch(administratorsListAction()),
        adviserGet: () => dispatch(adviserGetAction(ownProps.match.params.adviserUuid)),
        noteAdd: (note: string) => dispatch(adviserNotesAddAction(ownProps.match.params.adviserUuid, note)),
        notesList: () => dispatch(adviserNotesListAction(ownProps.match.params.adviserUuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Notes);
